<template>
  <div>
    <!-- Header -->
    <div class="content-header">
      <div class="content-header-left">
        <div class="content-header-title">
          <h2 class="content-title">Nuevo Contacto</h2>
          <p class="content-subtitle">Crea un nuevo contacto para una empresa</p>
        </div>
      </div>
      <div class="content-header-right">
        <b-button
          variant="outline-secondary"
          @click="$router.push({ name: 'contactos-lista' })"
        >
          <feather-icon icon="ArrowLeftIcon" class="mr-50" />
          Volver a la Lista
        </b-button>
      </div>
    </div>

    <!-- Form -->
    <b-card>
      <b-form @submit.prevent="guardarContacto">
        <b-row>
          <!-- Información Personal -->
          <b-col md="6">
            <h5 class="mb-3">Información Personal</h5>
            
            <b-form-group
              label="Nombre *"
              label-for="nombre"
              :invalid-feedback="errors.nombre"
              :state="errors.nombre ? false : null"
            >
              <b-form-input
                id="nombre"
                v-model="form.nombre"
                placeholder="Nombre completo del contacto"
                :state="errors.nombre ? false : null"
                @input="limpiarError('nombre')"
                required
              />
            </b-form-group>

            <b-form-group
              label="Cargo"
              label-for="cargo"
              :invalid-feedback="errors.cargo"
              :state="errors.cargo ? false : null"
            >
              <b-form-input
                id="cargo"
                v-model="form.cargo"
                placeholder="Cargo o posición en la empresa"
                :state="errors.cargo ? false : null"
              />
            </b-form-group>

            <b-form-group
              label="Teléfono"
              label-for="telefono"
              :invalid-feedback="errors.telefono"
              :state="errors.telefono ? false : null"
            >
              <b-form-input
                id="telefono"
                v-model="form.telefono"
                placeholder="Número de teléfono"
                :state="errors.telefono ? false : null"
              />
            </b-form-group>

            <b-form-group
              label="Email"
              label-for="email"
              :invalid-feedback="errors.email"
              :state="errors.email ? false : null"
            >
              <b-form-input
                id="email"
                v-model="form.email"
                type="email"
                placeholder="Correo electrónico"
                :state="errors.email ? false : null"
              />
            </b-form-group>

            <b-form-group
              label="LinkedIn"
              label-for="linkedin"
              :invalid-feedback="errors.linkedin"
              :state="errors.linkedin ? false : null"
            >
              <b-form-input
                id="linkedin"
                v-model="form.linkedin"
                placeholder="URL del perfil de LinkedIn"
                :state="errors.linkedin ? false : null"
              />
            </b-form-group>
          </b-col>

          <!-- Información de Empresa -->
          <b-col md="6">
            <h5 class="mb-3">Información de Empresa</h5>
            
            <b-form-group
              label="Empresa *"
              label-for="empresa"
              :invalid-feedback="errors.empresa"
              :state="errors.empresa ? false : null"
            >
              <div class="d-flex gap-2">
                <v-select
                  v-model="form.empresa"
                  :options="empresasOptions"
                  placeholder="Buscar y seleccionar empresa..."
                  :state="errors.empresa ? false : null"
                  :filterable="true"
                  :searchable="true"
                  :clearable="true"
                  :loading="loadingEmpresas"
                  label="nombre"
                  :reduce="empresa => empresa.idEmpresa"
                  @search="buscarEmpresas"
                  @input="limpiarError('empresa')"
                  required
                  class="flex-grow-1"
                >
                <template #no-options>
                  <div class="text-center py-2">
                    <span v-if="loadingEmpresas">Cargando empresas...</span>
                    <span v-else>No se encontraron empresas</span>
                  </div>
                </template>
                <template #option="{ nombre, direccion, telefono }">
                  <div>
                    <div class="font-weight-bold">{{ nombre }}</div>
                    <small class="text-muted" v-if="direccion">{{ direccion }}</small>
                    <small class="text-muted" v-if="telefono">{{ telefono }}</small>
                  </div>
                </template>
                  <template #selected-option="{ nombre }">
                    <span>{{ nombre }}</span>
                  </template>
                </v-select>
                <b-button 
                  variant="outline-primary" 
                  size="sm" 
                  @click="mostrarModalNuevaEmpresa"
                  title="Crear nueva empresa"
                >
                  <feather-icon icon="PlusIcon" size="16" />
                </b-button>
              </div>
            </b-form-group>

            <b-form-group
              label="Observaciones"
              label-for="observaciones"
              :invalid-feedback="errors.observaciones"
              :state="errors.observaciones ? false : null"
            >
              <b-form-textarea
                id="observaciones"
                v-model="form.observaciones"
                placeholder="Observaciones adicionales sobre el contacto"
                rows="4"
                :state="errors.observaciones ? false : null"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Form Actions -->
        <hr class="my-4">
        <div class="d-flex justify-content-end">
          <b-button
            type="button"
            variant="outline-secondary"
            class="mr-2"
            @click="$router.push({ name: 'contactos-lista' })"
          >
            Cancelar
          </b-button>
          <b-button
            type="submit"
            variant="primary"
            :disabled="loading"
          >
            <b-spinner
              v-if="loading"
              small
              class="mr-1"
            />
            <span v-if="loading">Guardando...</span>
            <span v-else>Guardar Contacto</span>
          </b-button>
        </div>
      </b-form>
    </b-card>

    <!-- Modal Nueva Empresa (alineado con ObrasNueva.vue) -->
    <b-modal
      id="modal-nueva-empresa"
      ref="modalNuevaEmpresa"
      title="Nueva Empresa"
      size="lg"
      @hidden="limpiarFormularioEmpresa"
      @ok="onOkCrearEmpresa"
      :ok-disabled="!formularioEmpresaValido || guardandoEmpresa"
    >
      <b-form>
        <b-row>
          <b-col md="6">
            <b-form-group label="Nombre de la Empresa" required>
              <b-form-input 
                v-model="nuevaEmpresa.nombre" 
                placeholder="Ingrese el nombre de la empresa"
                required 
                :state="mostrarErroresEmpresa ? validacionesEmpresa.nombre : null"
              />
              <b-form-invalid-feedback v-if="mostrarErroresEmpresa && validacionesEmpresa.nombre === false">
                El nombre es obligatorio
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group label="CIF" required>
              <b-form-input 
                v-model="nuevaEmpresa.cif" 
                placeholder="Ingrese el CIF de la empresa"
                required 
                :state="mostrarErroresEmpresa ? validacionesEmpresa.cif : null"
              />
              <b-form-invalid-feedback v-if="mostrarErroresEmpresa && validacionesEmpresa.cif === false">
                El CIF es obligatorio
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group label="Tipo de Empresa" required>
              <b-form-select 
                v-model="nuevaEmpresa.idTipoEmpresa" 
                :options="tiposEmpresa"
                value-field="idTipoEmpresa"
                text-field="nombre"
                placeholder="Seleccione un tipo de empresa"
                required
                :state="mostrarErroresEmpresa ? validacionesEmpresa.tipoEmpresa : null"
              />
              <b-form-invalid-feedback v-if="mostrarErroresEmpresa && validacionesEmpresa.tipoEmpresa === false">
                El tipo de empresa es obligatorio
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>

          <b-col md="6">
            <b-form-group label="Dirección" required>
              <b-form-textarea 
                v-model="nuevaEmpresa.direccion" 
                placeholder="Ingrese la dirección de la empresa"
                rows="3"
                required 
                :state="mostrarErroresEmpresa ? validacionesEmpresa.direccion : null"
              />
              <b-form-invalid-feedback v-if="mostrarErroresEmpresa && validacionesEmpresa.direccion === false">
                La dirección es obligatoria
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group label="Email">
              <b-form-input 
                v-model="nuevaEmpresa.email" 
                type="email"
                placeholder="email@empresa.com"
                :state="mostrarErroresEmpresa ? validacionesEmpresa.email : null"
              />
              <b-form-invalid-feedback v-if="mostrarErroresEmpresa && validacionesEmpresa.email === false">
                Formato de email inválido
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group label="Teléfono">
              <b-form-input 
                v-model="nuevaEmpresa.telefono" 
                placeholder="+34 123 456 789"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </b-form>

      <template #modal-footer="{ ok, cancel }">
        <b-button variant="outline-secondary" @click="cancel">
          Cancelar
        </b-button>
        <b-button 
          variant="primary" 
          @click="ok"
          :disabled="!formularioEmpresaValido || guardandoEmpresa"
        >
          <b-spinner v-if="guardandoEmpresa" small class="mr-1" />
          {{ guardandoEmpresa ? 'Creando...' : 'Crear Empresa' }}
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'
import { EmpresaService } from '@/services/empresaService'
import { TipoEmpresaService } from '@/services/tipoEmpresaService'
import { ContactoService } from '@/services/contactoService'

export default {
  name: 'ContactosNuevo',
  components: {
    vSelect,
  },
  data() {
    return {
      loading: false,
      loadingEmpresas: false,
      empresasOptions: [],
      // Modal Nueva Empresa (alineado con ObrasNueva.vue)
      nuevaEmpresa: {
        nombre: '',
        cif: '',
        direccion: '',
        idTipoEmpresa: null,
        email: '',
        telefono: ''
      },
      tiposEmpresa: [],
      guardandoEmpresa: false,
      validacionesEmpresa: {
        nombre: null,
        cif: null,
        direccion: null,
        tipoEmpresa: null,
        email: null
      },
      mostrarErroresEmpresa: false,
      // Formulario
      form: {
        nombre: '',
        cargo: '',
        telefono: '',
        email: '',
        linkedin: '',
        observaciones: '',
        empresa: null,
      },
      // Errores
      errors: {},
    }
  },
  async mounted() {
    this.cargarEmpresas()
    await this.cargarTiposEmpresa()
  },
  computed: {
    formularioEmpresaValido() {
      return this.validacionesEmpresa.nombre && 
             this.validacionesEmpresa.cif && 
             this.validacionesEmpresa.direccion && 
             this.validacionesEmpresa.tipoEmpresa &&
             this.validacionesEmpresa.email !== false
    }
  },
  methods: {
      limpiarError(campo) {
        if (this.errors[campo]) {
          this.$delete(this.errors, campo)
        }
      },
      onOkCrearEmpresa(bvModalEvt) {
        if (bvModalEvt && typeof bvModalEvt.preventDefault === 'function') {
          bvModalEvt.preventDefault()
        }
        this.mostrarErroresEmpresa = true
        this.crearEmpresa()
      },
      async cargarTiposEmpresa() {
        try {
          this.tiposEmpresa = await TipoEmpresaService.getAll()
        } catch (error) {
          console.error('Error cargando tipos de empresa:', error)
          this.tiposEmpresa = []
        }
      },
      mostrarModalNuevaEmpresa() {
        this.$refs.modalNuevaEmpresa.show()
      },
      limpiarFormularioEmpresa() {
        this.nuevaEmpresa = {
          nombre: '',
          cif: '',
          direccion: '',
          idTipoEmpresa: null,
          email: '',
          telefono: ''
        }
        this.validacionesEmpresa = {
          nombre: null,
          cif: null,
          direccion: null,
          tipoEmpresa: null,
          email: null
        }
        this.mostrarErroresEmpresa = false
      },
      validarFormularioEmpresa() {
        this.validacionesEmpresa.nombre = this.nuevaEmpresa.nombre && this.nuevaEmpresa.nombre.trim().length > 0
        this.validacionesEmpresa.cif = this.nuevaEmpresa.cif && this.nuevaEmpresa.cif.trim().length > 0
        this.validacionesEmpresa.direccion = this.nuevaEmpresa.direccion && this.nuevaEmpresa.direccion.trim().length > 0
        this.validacionesEmpresa.tipoEmpresa = this.nuevaEmpresa.idTipoEmpresa !== null
        if (this.nuevaEmpresa.email && this.nuevaEmpresa.email.trim()) {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
          this.validacionesEmpresa.email = emailRegex.test(this.nuevaEmpresa.email)
        } else {
          this.validacionesEmpresa.email = null
        }
      },
      async crearEmpresa() {
        this.validarFormularioEmpresa()
        if (!this.formularioEmpresaValido) {
          // this.$toast.error('Por favor, corrige los errores en el formulario')
          return
        }
        this.guardandoEmpresa = true
        try {
          const datosEmpresa = {
            ...this.nuevaEmpresa,
            web: undefined,
            observaciones: undefined
          }
          const empresaCreada = await EmpresaService.create(datosEmpresa)
          this.empresasOptions = [empresaCreada, ...this.empresasOptions].sort((a, b) => a.nombre.localeCompare(b.nombre))
          this.form.empresa = empresaCreada.idEmpresa
          this.$refs.modalNuevaEmpresa.hide()
          this.limpiarFormularioEmpresa()
        } catch (error) {
          console.error('Error al crear la empresa', error)
          const status = error && error.response && error.response.status
          const backendMsg = error && error.response && (error.response.data && (error.response.data.message || error.response.data.error || error.response.data))
          if (status === 409 || (typeof backendMsg === 'string' && backendMsg.toLowerCase().includes('ya existe'))) {
            if (this.$toast) this.$toast.error('Ya existe una empresa con ese CIF')
            this.validacionesEmpresa.cif = false
          } else if (this.$toast) {
            this.$toast.error('No se pudo crear la empresa')
          }
        } finally {
          this.guardandoEmpresa = false
        }
      },
    async cargarEmpresas(termino = '') {
      if (this.loadingEmpresas) return
      this.loadingEmpresas = true
      try {
        console.debug('[ContactosNuevo] cargarEmpresas()', { termino })
        if (termino && termino.trim().length >= 2) {
          console.debug('[ContactosNuevo] Buscando empresas por nombre (>=2 chars)')
          this.empresasOptions = await EmpresaService.searchByName(termino.trim())
        } else {
          console.debug('[ContactosNuevo] Cargando todas las empresas')
          this.empresasOptions = await EmpresaService.getAll()
        }
        console.debug('[ContactosNuevo] Empresas recibidas:', Array.isArray(this.empresasOptions) ? this.empresasOptions.length : 0)
      } catch (error) {
        console.error('Error al cargar empresas:', error)
        // this.$toast.error('Error al cargar las empresas')
      } finally {
        this.loadingEmpresas = false
      }
    },

    buscarEmpresas(termino) {
      console.debug('[ContactosNuevo] buscarEmpresas()', { termino })
      if (termino && termino.length >= 2) {
        this.cargarEmpresas(termino)
      } else if (termino.length === 0) {
        this.cargarEmpresas()
      }
    },

    validarFormulario() {
      // Limpiar errores previos
      const nuevosErrores = {}
      let esValido = true

      if (!this.form.nombre || this.form.nombre.trim() === '') {
        nuevosErrores.nombre = 'El nombre es obligatorio'
        esValido = false
      } else if (this.form.nombre.length > 300) {
        nuevosErrores.nombre = 'El nombre no puede exceder 300 caracteres'
        esValido = false
      }

      if (this.form.cargo && this.form.cargo.length > 100) {
        nuevosErrores.cargo = 'El cargo no puede exceder 100 caracteres'
        esValido = false
      }

      if (this.form.telefono && this.form.telefono.length > 100) {
        nuevosErrores.telefono = 'El teléfono no puede exceder 100 caracteres'
        esValido = false
      }

      if (this.form.email) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(this.form.email)) {
          nuevosErrores.email = 'El formato del email no es válido'
          esValido = false
        } else if (this.form.email.length > 100) {
          nuevosErrores.email = 'El email no puede exceder 100 caracteres'
          esValido = false
        }
      }

      if (this.form.linkedin && this.form.linkedin.length > 500) {
        nuevosErrores.linkedin = 'El LinkedIn no puede exceder 500 caracteres'
        esValido = false
      }

      if (this.form.observaciones && this.form.observaciones.length > 2000) {
        nuevosErrores.observaciones = 'Las observaciones no pueden exceder 2000 caracteres'
        esValido = false
      }

      if (!this.form.empresa) {
        nuevosErrores.empresa = 'La empresa es obligatoria'
        esValido = false
      }

      // Actualizar el objeto de errores de manera reactiva
      this.errors = nuevosErrores

      return esValido
    },

    async guardarContacto() {
      if (!this.validarFormulario()) {
        console.log('Errores de validación:', this.errors)
        // Forzar actualización de la vista para mostrar los errores
        this.$nextTick(() => {
          // Hacer scroll al primer campo con error
          const formGroups = document.querySelectorAll('.form-group')
          for (const group of formGroups) {
            const input = group.querySelector('input.is-invalid, .v-select.is-invalid')
            const target = input
            if (target) {
              target.scrollIntoView({ behavior: 'smooth', block: 'center' })
              if (target.tagName === 'INPUT') {
                target.focus()
              }
              break
            }
          }
        })
        return
      }

      this.loading = true
      try {
        const contactoData = {
          nombre: this.form.nombre.trim(),
          cargo: this.form.cargo.trim() || null,
          telefono: this.form.telefono.trim() || null,
          email: this.form.email.trim() || null,
          linkedin: this.form.linkedin.trim() || null,
          observaciones: this.form.observaciones.trim() || null,
          idEmpresa: this.form.empresa,
        }

        const creado = await ContactoService.create(contactoData)
        console.log('Contacto creado:', creado)
        // this.$toast.success('Contacto creado correctamente')
        this.$router.push({ name: 'contactos-lista' })
      } catch (error) {
        console.error('Error al crear contacto:', error)
        // this.$toast.error('Error al crear el contacto')
      } finally {
        this.loading = false
      }
    },
  },
  watch: {
    nuevaEmpresa: {
      handler() {
        this.validarFormularioEmpresa()
      },
      deep: true
    }
  }
}
</script>

<style scoped>
.content-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.content-header-title h2 {
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.content-subtitle {
  color: #6c757d;
  margin-bottom: 0;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group h5 {
  color: #495057;
  font-weight: 600;
  margin-bottom: 1rem;
}
</style>
